import { useCallback, useEffect, useState } from "react";

type QueryFunction<T> = () => Promise<T>;
type UseFetcherOption<T> = {
  queryFn: QueryFunction<T>;
  queryKey?: Array<unknown>;
  enabled?: boolean;
};
type ApiCallInfoType<T> = {
  data: T | undefined;
  isLoading: boolean;
  error: boolean;
  errorCode: number | null;
  errorPayload: unknown | null;
};
export type FetchError = {
  status: number;
  message: unknown;
};
export const useFetcherV2 = <T>(options: UseFetcherOption<T>) => {
  const enabled = options.enabled ?? true;
  const [apiCallInfo, setApiCallInfo] = useState<ApiCallInfoType<T>>({
    isLoading: enabled,
    error: false,
    errorCode: null,
    errorPayload: null,
    data: undefined
  });
  const { queryFn, queryKey = [] } = options;

  const fetchData = useCallback(async () => {
    setApiCallInfo({
      isLoading: true,
      error: false,
      errorCode: null,
      data: undefined,
      errorPayload: null
    });
    try {
      const result = await queryFn();
      setApiCallInfo({
        error: false,
        errorCode: null,
        isLoading: false,
        data: result,
        errorPayload: null
      });
    } catch (error: unknown) {
      setApiCallInfo({
        data: undefined,
        isLoading: false,
        error: true,
        errorCode: (error as FetchError)?.status,
        errorPayload: error
      });
    }
  }, [...queryKey]);

  useEffect(() => {
    if (enabled) {
      fetchData();
    } else {
      setApiCallInfo((previous) => ({
        ...previous,
        isLoading: false
      }));
    }
  }, [enabled, ...queryKey]);

  return { ...apiCallInfo, refetch: fetchData };
};
